/**
 * Dialog de detalhe do recurso (clique na linha): todos os campos + 'Tickets
 * abertos: N' + campos personalizados (GET/PUT /resources/:id/entities).
 */
import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import type { EquipmentDetail } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import { formatDateBR } from '@/features/billing/billing-format';
import { equipmentDetailOptions, errorMessage } from './resources-queries';
import { EquipmentStatusBadge, EquipmentWarranty } from './resources-format';
import { EquipmentEntitiesSection } from './equipment-entities-section';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm break-words">{children}</div>
    </div>
  );
}

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

export function ResourceDetailDialog({
  equipmentId,
  onEdit,
  onClose,
}: {
  equipmentId: number;
  onEdit: (equipment: EquipmentDetail) => void;
  onClose: () => void;
}) {
  const detailQ = useQuery(equipmentDetailOptions(equipmentId));
  const eq = detailQ.data;

  return (
    <Dialog open onClose={onClose} title={eq?.name ?? 'Recurso'} className="sm:max-w-2xl">
      {detailQ.isPending ? (
        <div aria-busy className="grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-28" />
            </div>
          ))}
        </div>
      ) : detailQ.isError ? (
        <EmptyState
          className="h-32"
          message={`Erro ao carregar recurso: ${errorMessage(detailQ.error)}`}
        />
      ) : eq ? (
        <div className="space-y-4">
          <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
            <Field label="Cliente">{eq.clientName}</Field>
            <Field label="Tipo">{eq.equipmentTypeName}</Field>
            <Field label="Grupo">{eq.equipmentGroupName ?? <Dash />}</Field>
            <Field label="Serial">
              {eq.serialNumber ? <span className="tabular-nums">{eq.serialNumber}</span> : <Dash />}
            </Field>
            <Field label="IMEI">
              {eq.imei ? <span className="tabular-nums">{eq.imei}</span> : <Dash />}
            </Field>
            <Field label="Status">
              <EquipmentStatusBadge active={eq.active} />
            </Field>
            <Field label="Data de aquisição">
              {eq.acquisitionDate ? (
                <span className="tabular-nums">{formatDateBR(eq.acquisitionDate)}</span>
              ) : (
                <Dash />
              )}
            </Field>
            <Field label="Garantia até">
              {eq.warrantyDate ? <EquipmentWarranty equipment={eq} /> : <Dash />}
            </Field>
            <Field label="Tickets abertos">
              <span className="font-semibold text-primary tabular-nums">{eq.openTicketsCount}</span>
            </Field>
            <Field label="Cadastrado em">
              <span className="tabular-nums">{formatDateTime(eq.createdAt)}</span>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Observações">
                {eq.notes ? <span className="whitespace-pre-wrap">{eq.notes}</span> : <Dash />}
              </Field>
            </div>
          </div>

          <div className="border-t border-divider pt-4">
            <EquipmentEntitiesSection equipmentId={eq.id} />
          </div>

          <div className="flex justify-end gap-2 border-t border-divider pt-3">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Fechar
            </Button>
            <Button type="button" size="sm" onClick={() => onEdit(eq)}>
              <Pencil className="size-3.5" />
              Editar
            </Button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
